import { buildAIPrompt, SYMPTOM_NAMES } from './aiPromptTemplate';

/**
 * Build context summary from digestion survey answers
 */
export function buildDigestionContext(answers) {
    const contextParts = [];

    // Safety check
    if (answers.safety) {
        if (answers.safety.includes('none')) {
            contextParts.push('- Good health condition, no specific risks');
        } else {
            contextParts.push('- Some health concerns to note');
        }
    }

    // Demographics
    if (answers.demographics) {
        const { age, weight } = answers.demographics;
        if (age) contextParts.push(`- Age group: ${age}`);
        if (weight) contextParts.push(`- Weight: ${weight}`);
    }

    // Main issues
    if (answers.mainIssue) {
        const issues = answers.mainIssue.map(issue => {
            switch (issue) {
                case 'bloating': return 'Prone to bloating';
                case 'poor_appetite': return 'Poor appetite';
                case 'indigestion': return 'Indigestion';
                case 'bowel_irregular': return 'Irregular bowel movements';
                default: return issue;
            }
        });
        contextParts.push(`- Main concerns: ${issues.join(', ')}`);
    }

    // Bloating details
    if (answers.bloatingTime) {
        const bloatingMap = {
            'immediately': 'Bloating immediately after meals',
            '1-2hrs': 'Bloating 1-2 hours after meals',
            'allday': 'Bloating all day'
        };
        contextParts.push(`- ${bloatingMap[answers.bloatingTime]}`);
    }

    // Appetite details
    if (answers.appetiteType) {
        const appetiteMap = {
            'no_appetite': 'No appetite at all',
            'early_fullness': 'Full after eating very little',
            'greasy_aversion': 'Aversion to greasy food'
        };
        contextParts.push(`- ${appetiteMap[answers.appetiteType]}`);
    }

    // Diet habits
    if (answers.dietHabits && answers.dietHabits.length > 0) {
        const habits = answers.dietHabits
            .filter(h => h !== 'regular')
            .map(habit => {
                switch (habit) {
                    case 'fast_eating': return 'Eat fast';
                    case 'greasy': return 'Eat greasy food often';
                    case 'cold_raw': return 'Love cold/raw food';
                    case 'overeating': return 'Binge eating';
                    default: return habit;
                }
            });
        if (habits.length > 0) {
            contextParts.push(`- Diet habits: ${habits.join(', ')}`);
        }
    }

    // Bowel movement
    if (answers.bowelMovement) {
        const bowelMap = {
            'constipation': 'Frequent constipation',
            'diarrhea': 'Prone to diarrhea',
            'alternating': 'Alternating constipation and diarrhea',
            'normal': 'Normal bowel movements'
        };
        contextParts.push(`- Bowel movements: ${bowelMap[answers.bowelMovement]}`);
    }

    // Other symptoms
    if (answers.otherSymptoms && answers.otherSymptoms.length > 0) {
        const symptoms = answers.otherSymptoms
            .filter(s => s !== 'none')
            .map(symptom => {
                switch (symptom) {
                    case 'belching': return 'Frequent belching';
                    case 'acid_reflux': return 'Acid reflux/heartburn';
                    case 'pain': return 'Abdominal/stomach pain';
                    case 'nausea': return 'Nausea';
                    default: return symptom;
                }
            });
        if (symptoms.length > 0) {
            contextParts.push(`- Other symptoms: ${symptoms.join(', ')}`);
        }
    }

    // Duration
    if (answers.duration) {
        const durationMap = {
            '<1month': 'Started within the last month',
            '1-3months': 'Has lasted 1-3 months',
            '>3months': 'Over 3 months (chronic issue)'
        };
        contextParts.push(`- Duration: ${durationMap[answers.duration]}`);
    }

    // Constitution
    if (answers.constitution && answers.constitution.length > 0) {
        const constitution = answers.constitution
            .filter(c => c !== 'none')
            .map(c => {
                switch (c) {
                    case 'cold_limbs': return 'Cold hands and feet';
                    case 'fatigue': return 'Easily fatigued';
                    case 'dry_bitter': return 'Dry/bitter mouth';
                    case 'thick_coating': return 'Thick tongue coating';
                    default: return c;
                }
            });
        if (constitution.length > 0) {
            contextParts.push(`- Constitution: ${constitution.join(', ')}`);
        }
    }

    return contextParts.join('\n');
}

/**
 * Build AI prompt for digestion questions
 */
export function buildDigestionPrompt(question, answers, userName) {
    const context = buildDigestionContext(answers);
  return buildAIPrompt(SYMPTOM_NAMES.digestion, context, question.text, userName);
}